import { isDefined } from '@yandex-int/messenger.utils';
import sdpTransform from 'sdp-transform';

import { RoomAgentBandwidth, RoomAgentXGoogleBitrate } from '../../config';
import { isH264ProfileLevelIdSupported } from '../helpers';
import { modifyFMTP } from './fmtp';
import { logger } from './logger';
import { applyPayloadTypes, PayloadTypeOptions } from './rtp';
import { SessionDescription, SharedDescriptionFields } from './types';

export type { PayloadTypeOptions } from './rtp';
export * from './types';

function getBandwidth(bandwidth: RoomAgentBandwidth, type: string) {
    switch (type) {
        case 'video':
            return bandwidth.video;
        case 'audio':
            return bandwidth.audio;
        default:
            return;
    }
}

function createBandwidthAttributeValue(limit: number): SharedDescriptionFields['bandwidth'] {
    return [
        {
            limit,
            type: 'TIAS',
        },
        {
            limit: Math.ceil(limit / 1000),
            type: 'AS',
        },
    ];
}

const H264_CODEC = 'H264';

function applyH264ProfileLevelId(
    sdp: SessionDescription,
    h264ProfileLevelIdDescriptors: Array<{ h264ProfileLevelId?: string; trackId: string }>
) {
    sdp.media
        .filter(({ type }) => type === 'video')
        .forEach((media) => {
            const descriptor = h264ProfileLevelIdDescriptors.find(({ trackId }) => media.msid?.includes(trackId));

            if (!descriptor || !descriptor.h264ProfileLevelId) {
                return;
            }

            if (!isH264ProfileLevelIdSupported(descriptor.h264ProfileLevelId)) {
                logger.warn(`unsupported level profile id [${descriptor.h264ProfileLevelId}]`, {
                    method: 'isH264ProfileLevelIdSupported',
                    additional: {
                        id: descriptor.h264ProfileLevelId,
                    },
                });
            }

            media.rtp.forEach((rtp) => {
                if (rtp.codec === H264_CODEC) {
                    media.fmtp.forEach((fmtp) => {
                        if (fmtp.payload === rtp.payload) {
                            fmtp.config = modifyFMTP(fmtp.config, {
                                'profile-level-id': descriptor.h264ProfileLevelId,
                            });
                        }
                    });
                }
            });
        });
}

function applyBandwidth(sdp: SessionDescription, bandwidth: RoomAgentBandwidth) {
    let total = 0;

    sdp.media.forEach((media) => {
        const limit = getBandwidth(bandwidth, media.type);

        if (isDefined(limit)) {
            total += limit;

            media.bandwidth = createBandwidthAttributeValue(limit);
        }
    });

    if (total) {
        sdp.bandwidth = createBandwidthAttributeValue(total);
    }
}

function applyContent(sdp: SessionDescription, contentDescriptors: Array<{ content?: string; trackId: string }>) {
    sdp.media.forEach((media) => {
        const descriptor = contentDescriptors.find(({ trackId }) => media.msid?.includes(trackId));

        if (descriptor?.content) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            media.content = descriptor.content;
        }
    });
}

interface TransformOutgoingSDPParams {
    bandwidth?: RoomAgentBandwidth;
    h264ProfileLevelIdDescriptors?: Array<{ h264ProfileLevelId?: string; trackId: string }>;
    contentDescriptors?: Array<{ content?: string; trackId: string }>;
    payloadTypes?: PayloadTypeOptions;
}

export function transformOutgoingSDP(sdp: string, params: TransformOutgoingSDPParams) {
    if (!Object.keys(params).length) {
        return sdp;
    }

    const parsed = sdpTransform.parse(sdp);

    const { h264ProfileLevelIdDescriptors, bandwidth, contentDescriptors, payloadTypes } = params;

    if (payloadTypes) {
        applyPayloadTypes(parsed, payloadTypes);
    }

    if (bandwidth) {
        applyBandwidth(parsed, bandwidth);
    }

    if (h264ProfileLevelIdDescriptors?.length) {
        applyH264ProfileLevelId(parsed, h264ProfileLevelIdDescriptors);
    }

    if (contentDescriptors?.length) {
        applyContent(parsed, contentDescriptors);
    }

    return sdpTransform.write(parsed);
}

/**
 * x-google bitrate params allow room-agent to start sending video to SIP with high bitrate right away
 */
function applyXGoogleBitrate(sdp: SessionDescription, { min, max, start }: RoomAgentXGoogleBitrate) {
    sdp.media.forEach((media) => {
        if (media.type === 'video') {
            media.fmtp.forEach((fmtp) => {
                fmtp.config = modifyFMTP(fmtp.config, {
                    'x-google-min-bitrate': min,
                    'x-google-max-bitrate': max,
                    'x-google-start-bitrate': start,
                });
            });
        }
    });
}

/**
 * Стоит делать только если браузер делает offer и сессии до этого не было (случай замены рум агента).
 * Нужно, т.к. некоторые устройства в answer могут добавить лишние application секции,
 * которых нет в нашем offer. Браузер их не генерит.
 */
function applyRemoveApplicationSections(incomingSdp: SessionDescription) {
    incomingSdp.media = incomingSdp.media.filter((m) => m.type !== 'application');
}

interface TransformIncomingSDPParams {
    xGoogleBitrate?: RoomAgentXGoogleBitrate;
    removeApplicationSections?: boolean;
}

export function transformIncomingSDP(sdp: string, params: TransformIncomingSDPParams) {
    const parsed = sdpTransform.parse(sdp);
    const { xGoogleBitrate, removeApplicationSections } = params;

    if (xGoogleBitrate) {
        applyXGoogleBitrate(parsed, xGoogleBitrate);
    }

    if (removeApplicationSections) {
        applyRemoveApplicationSections(parsed);
    }

    return sdpTransform.write(parsed);
}

/**
 * Zoom rooms send SDP with multiple video sections
 * marking the with `a=content:xxx` attribute.
 *
 * Known so far are:
 * a=content:main
 * a=content:slides
 *
 * CISCO codes on the other hand send only one video section
 * and do not use `a=content:xxx` attribute.
 * To support both cases this function finds video section index (among all media sections including audio)
 * that we assume is the section with the actual video.
 *
 * IMPORTANT NOTE: You can not pass an SDP from RTCPeerConnection object (e.g. this.connection.remoteDescription.sdp)
 * because browser removes some attributes (a=content:xxx) after SDP has been applied. Use original SDP string.
 */
export function findMainVideoSectionMid(sdp: string): string | null {
    const parsed = sdpTransform.parse(sdp);

    let mid: string | null = null;

    for (let i = 0; i < parsed.media.length; i++) {
        const media = parsed.media[i];

        if (media.type !== 'video') {
            continue;
        }

        mid = String(media.mid || i);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore. Property content does not exist on type
        if (media.content === 'main') {
            break;
        }
    }

    return mid;
}

export function getMediaSectionCount(sdp: string): number | null {
    return sdp.match(/^m=(audio|video)/gm)?.length || null;
}
